import React, { useMemo, useState, useRef } from "react";
import { View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  ActivityIndicator
} from "react-native";
import { useTranslation } from 'react-i18next';
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import DownArrow from "../../../../../../../../assets/images/DownArrow.png";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import { postFeeLookup } from "../../../../../services/common.service";
import type { FeeInfo } from "../../../../../view-models/feeInfo";

import ReactCountryFlag from 'react-country-flag';
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);


type Props = {
  onContinue: () => void;
  onEditStep?: (key: string) => void;
};

export default function ReviewAndEditStep({ onEditStep, onContinue }: Props) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const [promoCode, setPromoCode] = useState("");
  const [applyLoading, setApplyLoading] = useState<boolean>(false);
  const [preApplyFees, setPreApplyFees] = useState<number | null>(null);
  const [postApplyFees, setPostApplyFees] = useState<number | null>(null);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const selectedCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const selectedStateName = useSessionStore((s: SessionState) => s.selectedDestinationStateName);
  const feeLookupParams = useSessionStore((s: SessionState) => (s as any).sendFeeLookupReviewEditStore);
  const setSendFeeLookupReviewEditStore = useSessionStore((s: SessionState) => (s as any).setSendFeeLookupReviewEditStore);
  const setLastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).setLastFeeLookupResponse);
  const setSelectedSendOptionStore = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const setPendingSendOptionStore = useSessionStore((s: SessionState) => (s as any).setPendingSendOption);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);
  const selectedDeliveryOptionStore = useSessionStore((s: SessionState) => (s as any).selectedDeliveryOptionStore);

  const option: any = pendingSendOption || selectedSendOption || null;
  // Selected delivery option FeeInfo derived reactively from store
  const selectedDeliveryFeeInfo: FeeInfo | undefined = useMemo(() => {
    const arr: FeeInfo[] = Array.isArray((lastFeeLookupResponse as any)?.FeeInfos)
      ? (lastFeeLookupResponse as any).FeeInfos
      : (Array.isArray((lastFeeLookupResponse as any)?.FeeInfo) ? (lastFeeLookupResponse as any).FeeInfo : []);
    const sel = ((selectedDeliveryOptionStore as any)?.DeliveryOption ?? '').toString().toUpperCase();
    if (!arr || arr.length === 0) return undefined;
    if (!sel) return arr[0];
    return arr.find((f: any) => {
      const opt = (f?.DeliveryOption ?? f?.DeliveryOptId ?? '').toString().toUpperCase();
      return !!opt && opt === sel;
    }) || arr[0];
  }, [lastFeeLookupResponse, selectedDeliveryOptionStore]);
  const summary = useMemo(() => {
    if (!option) {
      return {
        sendAmount: 0,
        sendCurrency: 'USD',
        receiveAmount: 0,
        receiveCurrency: '—',
        exchangeRate: 0,
        fees: 0,
        total: 0,
      };
    }
    const sendCurrency: string = option?.SendAmounts?.SendCurrency || 'USD';
    const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
    const receiveCurrency: string = option?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (option?.ValidReceiveCurrency || '')
      : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '');
    const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0);
    const exchangeRateNum: number = Number(FeeLookupHelper.getExchangeRate(option) || 0);
    const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
    const totalBase: number = Number(option?.TotalAmount || 0);
    const total: number = totalBase;
    return { sendAmount, sendCurrency, receiveAmount, receiveCurrency, exchangeRate: exchangeRateNum, fees, total };
  }, [pendingSendOption, selectedSendOption]);

  console.log('pendingSendOption........', pendingSendOption);

  const alpha2 = useMemo(() => {
    const a3 = (selectedCountryCode || '').toString().toUpperCase();
    return countries.alpha3ToAlpha2(a3) || '';
  }, [selectedCountryCode]);

  const countryCodeToEmoji = (code: string) => {
    if (!code) return "";
    const upper = code.toUpperCase();
    if (upper.length !== 2) return "";
    const base = 127397;
    return [...upper].map((c) => String.fromCodePoint(base + c.charCodeAt(0))).join("");
  };

  const handleChange = (text: string, index: number) => {
    if (/^\d*$/.test(text)) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    }
  };

  const handleApplyPromoCode = async() => {
    setApplyLoading(true);
    setPreApplyFees(summary.fees);
    const updatedParams = {
                ...(feeLookupParams || {}),
                promoCodes: promoCode ? [promoCode] : undefined,
                deliveryOption: ((selectedDeliveryOptionStore as any)?.DeliveryOption ?? undefined),
                allOptions: false,
              } as any;
              try { setSendFeeLookupReviewEditStore?.(updatedParams); } catch { }
              const feeResp = await postFeeLookup(updatedParams as any);
              console.log('Promo code applied:', promoCode, 'feeLookupParams:', updatedParams, 'feeResp:', feeResp);
              // Inspect potential error envelope in fee lookup response
              try {
                const rawData: any = (feeResp && (feeResp as any).data !== undefined) ? (feeResp as any).data : feeResp;
                let parsed: any = rawData;
                if (typeof rawData === 'string') {
                  try { parsed = JSON.parse(rawData); } catch {}
                }
                // Save parsed payload so FeeInfos are available for selection
                try { setLastFeeLookupResponse?.(parsed); } catch {}

                const feeInfos: any[] = Array.isArray(parsed?.FeeInfos) ? parsed?.FeeInfos : (Array.isArray(parsed?.FeeInfo) ? parsed?.FeeInfo : []);
                // Try to find the updated option matching current selection
                const current = option || {};
                const match = feeInfos.find((f: any) => {
                  const sameDisplay = (f?.DeliveryOptDisplayName || '') === (current?.DeliveryOptDisplayName || '');
                  const sameAgent = (f?.ReceiveAgentId || f?.ReceiveAgentID) === (current?.ReceiveAgentId || current?.ReceiveAgentID);
                  return sameDisplay || sameAgent;
                }) || feeInfos[0];

                if (!match) {
                  return;
                }

                // Check promotion status
                const promoArray: any[] = Array.isArray(match?.PromotionInfo) ? match?.PromotionInfo : [];
                const hasError = promoArray.some((p: any) => !!(p?.PromotionErrorCode || p?.promotionErrorCode));
                if (!hasError) {
                  // Apply discount to fees if necessary (prefer server-calculated values)
                  const discountAmt = Number(match?.SendAmounts?.TotalDiscountAmount ?? 0) || Number(promoArray?.reduce((sum: number, p: any) => sum + Number(p?.PromotionDiscountAmount || 0), 0));
                  const originalFees = Number(match?.SendAmounts?.TotalSendFees ?? 0);
                  const taxes = Number(match?.SendAmounts?.TotalSendTaxes ?? 0);
                  const sendAmt = Number(match?.SendAmounts?.SendAmount ?? 0);
                  const discountedFees = Math.max(0, originalFees - (discountAmt || 0));

                  const totalAmountToCollect = (typeof match?.SendAmounts?.TotalAmountToCollect === 'number')
                    ? Number(match?.SendAmounts?.TotalAmountToCollect)
                    : (sendAmt + discountedFees + taxes);

                  const updatedOption = {
                    ...match,
                    SendAmounts: {
                      ...(match?.SendAmounts || {}),
                      TotalDiscountAmount: discountAmt || match?.SendAmounts?.TotalDiscountAmount,
                      TotalSendFees: discountedFees,
                      TotalAmountToCollect: totalAmountToCollect,
                    },
                    TotalAmount: typeof match?.TotalAmount === 'number' ? match?.TotalAmount : totalAmountToCollect,
                  } as any;

                  // Capture new fees (discount + discountedFees) for display
                  try { setPostApplyFees((discountAmt || 0) + (discountedFees || 0)); } catch {}
                  try { setSelectedSendOptionStore?.(updatedOption); } catch {}
                  try { setPendingSendOptionStore?.(updatedOption); } catch {}
                }
              } catch {}
              setApplyLoading(false);
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("REVIEW_SUBTITLE")}</Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{selectedDeliveryFeeInfo?.SendAmounts?.SendAmount}</Text>
            <Text style={styles.currencyText}>{selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
          </View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("REVIEW_THEY_RECEIVE")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{selectedDeliveryFeeInfo?.ReceiveAmounts?.ReceiveAmount}</Text>
            <Text style={styles.currencyText}>{selectedDeliveryFeeInfo?.ReceiveAmounts?.ReceiveCurrency}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('sendingAmountEntry')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 38}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("REVIEW_DESTINATION_COUNTRY")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
          {Platform.OS === 'web' ? (
              alpha2 ? (
                <ReactCountryFlag
                  countryCode={alpha2}
                  svg
                  style={{ width: 20, height: 20, borderRadius: 50 }}
                  title={alpha2}
                />
              ) : (
                <Text style={{ fontSize: 16 }}>🌍</Text>
              )
            ) : (
              <Text style={{ fontSize: 16 }}>{countryCodeToEmoji(alpha2) || '🌍'}</Text>
            )}
            <Text style={styles.CountryText}>{selectedCountryName}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: '#FFFFFF',}]}
            onPress={() => onEditStep?.('country')}>
            <Image
              source={DownArrow}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
          </TouchableOpacity>
        </View>
      </View>
      { (selectedCountryCode === 'USA' || selectedCountryCode === 'CAN') &&
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 38}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>Destination State</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <Image
              source={EnglishFlag}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
            <Text style={styles.CountryText}>{selectedStateName}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: '#FFFFFF',}]}
            onPress={() => onEditStep?.('state')}>
            <Image
              source={DownArrow}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
          </TouchableOpacity>
        </View>
      </View>
      }
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 38}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>How they receive</Text>
          <Text style={[styles.pickUpText, { width: 250}]}>{selectedDeliveryFeeInfo?.DeliveryOptDisplayName}</Text>
        </View>
        <View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('howTheyReceive')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.promoContainer}>
        <TextInput
          style={styles.promoInput}
          placeholder="Promo code"
          value={promoCode}
          onChangeText={setPromoCode}
        />
        <TouchableOpacity style={styles.promoApplyButton} 
          disabled={applyLoading}
          onPress={async () => {
            handleApplyPromoCode()
          }}
        >
          {applyLoading ? (
            <ActivityIndicator size="small" color={Colors.applyText} style={{ paddingVertical: 4, paddingHorizontal: 6 }} />
          ) : (
            <Text style={styles.applyText}>Apply</Text>
          )}
        </TouchableOpacity>
      </View>
      {/* Terms */}
      <View style={{ marginTop: 16 }}>
        <Text style={styles.terms}>
          I agree to MoneyGram’s <Text style={styles.link}>Terms and Conditions</Text> and confirm I am the sole beneficial owner of the assets, and am acting solely on my own profile. By continuing with this transaction I consent to the collection, use, disclosure, and transfer (including cross-border transfer) of my personal information as described in our Privacy Policy.
        </Text>
        <Text style={[styles.terms, { marginTop: 16, marginBottom: 24}]}>
          <Text style={styles.bold}>Exchange rate</Text>{"\n"}
          In addition to transfer fees, a currency exchange rate, also determined by MoneyGram, may also apply. MoneyGram may also make money from currency exchange. When selecting a money transfer operator, carefully compare both transfer fees and currency exchange rates. Transfer fees may vary depending on the amount being transferred and the delivery method. Transfer fee and currency exchange rate may be subject to change by MoneyGram without notice.
        </Text>
      </View>
      {/* Fees and CTA */}
      <GradientBackground style={styles.card}>
        <View><View style={styles.viewTap}/></View>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_EXCHANGE_RATE")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>1 {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency} = {summary.exchangeRate.toFixed(4)} {selectedDeliveryFeeInfo?.ReceiveAmounts?.ReceiveCurrency}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_FEES")}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'baseline'}}>
            {(preApplyFees !== null && postApplyFees !== null && Number(preApplyFees.toFixed(2)) !== Number(postApplyFees.toFixed(2))) ? (
              <>
                <Text style={[styles.oldPrice, { textDecorationLine: 'line-through', marginRight: 8 }]}>{preApplyFees.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
                <Text style={styles.newPrice}>{postApplyFees.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
              </>
            ) : (
              <Text style={styles.oldPrice}>{summary.fees.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
            )}
          </View>
        </View>
        <View style={styles.row}>
          <Text style={[styles.feeLabel, {textDecorationLine: "underline"}]}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{summary.total.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity
          onPress={onContinue}
          activeOpacity={0.8}
          style={styles.continueButton}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
      </GradientBackground>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    justifyContent: "flex-start",
    // marginTop: 16,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 48
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Rubik',
    color: Colors.infoSubValue,
    lineHeight: 16,
    alignSelf: 'stretch'
    // marginBottom: 8,
  },
    exchangeRate: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '500',
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pickUpText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.black,
    lineHeight: 20
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    lineHeight: 38
  },
  amountTextGreen: {
    color: '#4CAF50',
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: '#8C8C8C',
    lineHeight: 18,
    paddingLeft: 6
  },
  editButton: {
    backgroundColor: '#E6E6E6',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 16
  },
  editText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: '#1F1F1F',
    lineHeight: 18
  },
  CountryText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#1F1F1F',
    lineHeight: 24,
    paddingLeft: 6
  },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  card: {
    alignItems: "center",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    marginBottom: 24,
    alignSelf: 'center'
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.termsAndConditionsBlue,
    fontWeight: "500",
  },
  promoContainer: {
    flexDirection: 'row',
    height: 56,
    padding: 24,
    borderRadius: 8,
    backgroundColor: Colors.searchCompanyBackground,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24
  },
  promoInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#B3B3B3',
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginRight: 12
  },
  promoApplyButton: {
    // paddingVertical: 14,
    // paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#B3B3B3'
  },
  applyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.applyText,
    lineHeight: 18,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  bold: { fontWeight: "600" },
  continueButton: {
    alignSelf: "center",
    backgroundColor: Colors.black,
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: Colors.black,
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
    lineHeight: 24
  },
});
